import React from "react";
import { X, Phone, Mail, Linkedin, MapPin, Download, QrCode, MessageCircle } from "lucide-react";
import { company, contact } from "../mock";

function buildVCard(p) {
  const parts = p.name.replace(/^Mr\.\s*/, "").split(" ");
  const first = parts[0] || "";
  const last = parts.slice(1).join(" ") || "";
  const cell = "+" + (p.whatsapp || "");
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${last};${first};;Mr.;`,
    `FN:${p.name}`,
    `ORG:${company.name}`,
    `TITLE:${p.role}`,
    `TEL;TYPE=CELL:${cell}`,
    `TEL;TYPE=WORK:${contact.phone.replace(/\s/g, "")}`,
    `EMAIL:${p.email}`,
    `URL:${p.linkedin}`,
    `ADR;TYPE=WORK:;;Niraj Industrial Estate, Andheri East;Mumbai;;400093;India`,
    "END:VCARD",
  ];
  return lines.join("\n");
}

export default function BusinessCard({ person, onClose }) {
  if (!person) return null;
  const vcard = buildVCard(person);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(vcard)}`;

  const download = () => {
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${person.name.replace(/\s+/g, "-")}-KSE.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0d1640]/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md">
        <button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white text-[#1a2a6b] flex items-center justify-center shadow-lg">
          <X size={20} />
        </button>

        {/* Card */}
        <div className="rounded-3xl overflow-hidden shadow-2xl">
          {/* Top brand band */}
          <div className="relative bg-gradient-to-br from-[#1a2a6b] to-[#2b3aa0] px-7 pt-8 pb-16 text-white">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-2xl">KSE</span>
              <span className="text-[10px] font-medium text-blue-200 tracking-[0.35em] uppercase">Inc.</span>
            </div>
            <p className="text-blue-200 text-xs mt-1 tracking-wide">Garment Manufacturing \u00B7 Est. 1998</p>
          </div>

          {/* Body */}
          <div className="bg-white px-7 pb-7 -mt-10 relative">
            <div className="flex items-end justify-between">
              <div className="w-20 h-20 rounded-2xl bg-[#1a2a6b] text-white flex items-center justify-center text-3xl font-extrabold shadow-lg border-4 border-white">
                {person.name.replace(/^Mr\.\s*/, "").charAt(0)}
              </div>
              <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="mb-1 w-10 h-10 rounded-full bg-[#0a66c2] text-white flex items-center justify-center shadow">
                <Linkedin size={18} />
              </a>
            </div>

            <h3 className="text-2xl font-extrabold text-[#1a2a6b] mt-4">{person.name}</h3>
            <p className="text-[#2b3aa0] font-semibold">{person.role}</p>
            <p className="text-slate-400 text-sm">{company.name}</p>

            <div className="mt-5 space-y-2.5">
              <a href={`tel:${person.phones[0]}`} className="flex items-center gap-3 text-slate-700 hover:text-[#2b3aa0]">
                <Phone size={16} className="text-[#2b3aa0]" /> <span className="font-medium">{person.phones[0]}</span>
              </a>
              <a href={`https://wa.me/${person.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-[#25D366]">
                <MessageCircle size={16} className="text-[#25D366]" /> <span className="font-medium">WhatsApp</span>
              </a>
              <a href={`mailto:${person.email}`} className="flex items-center gap-3 text-slate-700 hover:text-[#2b3aa0]">
                <Mail size={16} className="text-[#2b3aa0]" /> <span className="font-medium break-all">{person.email}</span>
              </a>
              <p className="flex items-start gap-3 text-slate-500 text-sm">
                <MapPin size={16} className="text-[#2b3aa0] mt-0.5 shrink-0" /> Andheri (East), Mumbai 400093, India
              </p>
            </div>

            {/* QR */}
            <div className="mt-6 flex items-center gap-4 bg-slate-50 rounded-2xl p-4">
              <img src={qrSrc} alt="Scan to save contact" className="w-24 h-24 rounded-lg bg-white" />
              <div>
                <div className="flex items-center gap-1.5 text-[#1a2a6b] font-bold text-sm">
                  <QrCode size={16} /> Scan to Save
                </div>
                <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                  Point your phone camera at the QR code to save this contact instantly.
                </p>
              </div>
            </div>

            <button onClick={download} className="kse-btn w-full justify-center bg-[#2b3aa0] hover:bg-[#1a2a6b] text-white py-3 mt-4">
              <Download size={17} /> Download Business Card (.vcf)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
